import * as React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Toggle } from '@/ui-kit/components/form/Toggle/Toggle';

import { ComponentLookup } from '@/ui-kit/domain/ComponentLookup';
import { useCallback, useMemo } from 'react';
import { IUICfgComponent, IUICfgComponentElementOptions, ComponentVisibility } from '@canvas-logic/clp-api-types';
import ThemeDefinition from '@/ui-kit/domain/ThemeDefinition';
import { useSlotLabel } from '@/localization/useSlotLabel';
import { useContainerLevel } from '@/ui-kit/contexts/ContainerLevelContext';

interface IProps {
  lookup: ComponentLookup;
  /** Image URL — rendered as `<img src={icon} />`, not as arbitrary nodes. */
  icon?: string;
  title?: string;
  viewElement: IUICfgComponent;
}

const ComponentSwitchCfgUIElement: React.FC<IProps> = ({ lookup, icon, viewElement }) => {
  const uiSettings = viewElement.uiControl as IUICfgComponentElementOptions;
  const tSlot = useSlotLabel(viewElement.source.componentKey);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = lookup.values.find((option) => option.value === event.target.checked);

      if (value) {
        lookup.select(value.id);
      }
    },
    [lookup],
  );

  // Check if component UI (title, description, switcher) should be visible
  // This does NOT affect products/attributes visibility - they render separately
  const isComponentUIVisible = useMemo(() => {
    const showComponent: ComponentVisibility = uiSettings.showComponent ?? '2+';
    switch (showComponent) {
      case 'always':
        return true;
      case 'never':
        return false;
      case '2+':
        return lookup.productData.length >= 2;
      default:
        return true;
    }
  }, [uiSettings.showComponent, lookup.productData.length]);

  const showTitle = uiSettings.showTitle;
  const showDescription = uiSettings.showDescription;
  const containerLevel = useContainerLevel();
  if (lookup.loading || !isComponentUIVisible) {
    return null;
  }
  const slotTitle = tSlot.title(String(lookup.texts.name ?? ''));
  const slotDescription = tSlot.desc(String(lookup.texts.description ?? ''));
  return (
    <fieldset className={classNames('cp-component-switch')} data-component-toggle={viewElement.source.componentKey}>
      <div className={'cp-component-switch__header'} data-title-level={containerLevel}>
        {icon && <img className={ThemeDefinition.selectorLabel.icon} src={icon} alt={slotTitle} />}
        {showTitle && <div className={'cp-component-switch__title'}>{slotTitle}</div>}
        {uiSettings.showSwitcher && (
          <div className={'cp-component-switch__control'}>
            <Toggle
              checked={!!lookup.current.value}
              disabled={false === lookup.componentSchema?.optional}
              onChange={handleChange}
            />
          </div>
        )}
      </div>
      {showDescription && <div className={'cp-component-switch__description paragraph-sm'}>{slotDescription}</div>}
    </fieldset>
  );
};

export default observer(ComponentSwitchCfgUIElement);
